import React, { useEffect, useState } from 'react';
import {
  Navbar as RBNavbar,
  Container,
  Nav,
  Offcanvas,
  ListGroup,
  Image,
} from 'react-bootstrap';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Navbar link base style
const navLinkBaseStyle = {
  position: 'relative',
  color: '#fff',
  fontWeight: 500,
  transition: 'color 0.3s ease',
  textTransform: 'uppercase',
  textDecoration: 'none',
  paddingBottom: '4px',
};

// Underline style for navbar + offcanvas links
const underlineStyle = (isActiveOrHover) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  height: '2px',
  width: isActiveOrHover ? '100%' : '0%',
  backgroundColor: '#ffc107',
  transition: 'width 0.3s ease',
});

// Offcanvas container style (blur + dark)
const offcanvasContainerStyle = (show) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.33)',
  backdropFilter: 'blur(10px)',
  color: '#fff',
  transform: show ? 'translateX(0)' : 'translateX(-100%)',
  opacity: show ? 1 : 0,
  transition: 'transform 0.4s ease-in-out, opacity 0.3s ease',
});

// Sidebar list item animation style
const sideItemStyle = (show, index, isLast = false) => ({
  backgroundColor: '#000',
  borderBottom: isLast ? 'none' : '1px solid #6c757d',
  padding: '0.75rem 1rem',
  opacity: show ? 1 : 0,
  transform: show ? 'translateX(0)' : 'translateX(-20px)',
  transition: 'all 0.4s ease',
  transitionDelay: show ? `${0.1 * (index + 1)}s` : '0s',
});

// Offcanvas link style
const offcanvasNavLinkStyle = {
  ...navLinkBaseStyle,
  color: '#fff',
};

// Custom toggler container style
const togglerBoxStyle = {
  position: 'relative',
  width: '24px',
  height: '16px',
};

// Base bar style for toggler lines
const barBase = {
  position: 'absolute',
  left: 0,
  width: '24px',
  height: '2px',
  backgroundColor: '#fff',
  transition: 'all 0.3s ease-in-out',
};

const bar1Style = (show) => ({
  ...barBase,
  top: show ? '7px' : '0px',
  transform: show ? 'rotate(45deg)' : 'none',
});

const bar2Style = (show) => ({
  ...barBase,
  top: '7px',
  opacity: show ? 0 : 1,
});

const bar3Style = (show) => ({
  ...barBase,
  top: show ? '7px' : '14px',
  transform: show ? 'rotate(-45deg)' : 'none',
});

// ---------- Component ----------

const Navbar = () => {
  const [show, setShow] = useState(false);
  const [hoverLink, setHoverLink] = useState(null);
  const [activeLink, setActiveLink] = useState('Home'); // default active like original

  useEffect(() => {
    AOS.init({
      duration: 600,
      easing: 'ease-in-out',
      once: true,
    });

    // Auto-close sidebar on desktop resize
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setShow(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { name: 'Home', link: '/' },
    { name: 'About', link: '#about' },
    { name: 'Services', link: '#service' },
    { name: 'Gallery', link: '/gallery' },
    { name: 'Contact', link: '#contact' },
  ];

  const handleNavClick = (name) => {
    setActiveLink(name);
    setShow(false);
  };

  return (
    <>
      {/* Top Navbar */}
      <RBNavbar
        expand='lg'
        variant='dark'
        fixed='top'
        data-aos='fade-down'
        style={{ backgroundColor: 'rgba(33, 34, 35, 0.61)' }}
        expanded={false}
        onToggle={() => {}}
      >
        <Container fluid style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
          {/* Brand */}
          <RBNavbar.Brand
            href='/'
            style={{ display: 'flex', alignItems: 'center' }}
            onClick={() => setActiveLink('Home')}
          >
            <Image
              src='img/logo.png'
              alt='Pravin'
              width={46}
              height={46}
              roundedCircle
              style={{
                marginRight: '0.5rem',
                border: '2px solid #ffc107',
              }}
            />
            <span style={{ fontWeight: 600 }}>Pravin BirdNetting</span>
          </RBNavbar.Brand>

          {/* Custom Toggler */}
          <RBNavbar.Toggle
            aria-controls='mobileSidebar'
            aria-expanded={show}
            aria-label='Toggle navigation'
            onClick={() => setShow((prev) => !prev)}
            style={{
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
            }}
          >
            <div style={togglerBoxStyle}>
              <div style={bar1Style(show)} />
              <div style={bar2Style(show)} />
              <div style={bar3Style(show)} />
            </div>
          </RBNavbar.Toggle>

          {/* Desktop Nav */}
          <RBNavbar.Collapse
            id='navbarNav'
            style={{ justifyContent: 'flex-end' }}
          >
            <Nav
              style={{
                display: 'flex',
                gap: '1.5rem',
                alignItems: 'center',
                textTransform: 'uppercase',
              }}
            >
              {navItems.map((item) => {
                const isActive = activeLink === item.name;
                const isHover = hoverLink === item.name;

                return (
                  <Nav.Link
                    key={item.name}
                    href={item.link}
                    onClick={() => handleNavClick(item.name)}
                    onMouseEnter={() => setHoverLink(item.name)}
                    onMouseLeave={() => setHoverLink(null)}
                    style={{
                      ...navLinkBaseStyle,
                      color: isActive || isHover ? '#ffc107' : '#fff',
                    }}
                  >
                    <span
                      style={{ position: 'relative', display: 'inline-block' }}
                    >
                      {item.name}
                      <span style={underlineStyle(isActive || isHover)} />
                    </span>
                  </Nav.Link>
                );
              })}
            </Nav>
          </RBNavbar.Collapse>
        </Container>
      </RBNavbar>

      {/* Offcanvas Sidebar */}
      <Offcanvas
        show={show}
        onHide={() => setShow(false)}
        placement='start'
        aria-labelledby='mobileSidebarLabel'
        style={offcanvasContainerStyle(show)}
      >
        {/* Header */}
        <Offcanvas.Header
          closeButton
          closeVariant='white'
          style={{
            borderBottom: '1px solid #6c757d',
          }}
        >
          <Offcanvas.Title
            id='mobileSidebarLabel'
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Image
              src='img/logo.png'
              width={36}
              height={36}
              roundedCircle
              style={{
                marginRight: '0.5rem',
                border: '2px solid #ffc107',
              }}
            />
            <span>BirdNet</span>
          </Offcanvas.Title>
        </Offcanvas.Header>

        {/* Body */}
        <Offcanvas.Body style={{ padding: 0 }}>
          <ListGroup variant='flush'>
            {navItems.map((item, index) => {
              const isLast = index === navItems.length - 1;
              const isActive = activeLink === item.name;
              const isHover = hoverLink === `side-${item.name}`;

              return (
                <ListGroup.Item
                  key={item.name}
                  style={sideItemStyle(show, index, isLast)}
                >
                  <Nav.Link
                    href={item.link}
                    onClick={() => handleNavClick(item.name)}
                    onMouseEnter={() => setHoverLink(`side-${item.name}`)}
                    onMouseLeave={() => setHoverLink(null)}
                    style={{
                      ...offcanvasNavLinkStyle,
                      color: isActive || isHover ? '#ffc107' : '#fff',
                    }}
                  >
                    <span
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                      }}
                    >
                      {item.name}
                      <span style={underlineStyle(isActive || isHover)} />
                    </span>
                  </Nav.Link>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default Navbar;
